import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { ColDef } from "ag-grid-community";

import analytics from "fond/analytics";
import { GetRowsParams } from "fond/attachments/hooks/useGetAttachmentRows";
import { Attachment } from "fond/types";

import FileViewerModal from "./FileViewerModal";
import GalleryView from "./GalleryView";
import ListView from "./ListView";
import { FileViewerTrackingContext, Sort, ViewMode } from "./types";

interface IProps {
  files: Attachment[];
  total: number;
  view: ViewMode;
  listViewColumnDefs?: ColDef[];
  listViewPagination?: boolean;
  selected: Attachment[];
  getRows: (params: GetRowsParams) => Promise<void>;
  onSelectionChange(files: Attachment[]): void;
  actions?: React.ReactNode;
  /**
   * Additional context for the file viewer, used for tracking purposes.
   * For example, "feature-attachments" or "project-attachments".
   * This is used to track the context in which the file viewer is being used.
   */
  trackingContext?: FileViewerTrackingContext;
}

const FilesViewer: React.FC<IProps> = ({
  files,
  total,
  view,
  listViewColumnDefs,
  listViewPagination = true,
  selected,
  getRows,
  onSelectionChange,
  actions,
  trackingContext,
}) => {
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);
  const [sort, setSort] = useState<Sort | null>(null);

  const handleZoomOpen = (index: number) => {
    analytics.track("file-viewer.file.preview.opened", { view, index, context: trackingContext });
    setInitialSlide(index);
    setIsZoomOpen(true);
  };

  const handleZoomClose = (closingIndex: number) => {
    analytics.track("file-viewer.file.preview.closed", { view, index: closingIndex, context: trackingContext });
    setIsZoomOpen(false);
  };

  const commonProps = {
    files,
    getRows,
    handleZoomOpen,
    onSelectionChange,
    selected,
    sort,
  };

  // Hide the count if it's zero since we'll show an "empty state" in the panel
  let fileCount = total > 0 ? `${total} ${total === 1 ? "file" : "files"}` : "";
  if (selected.length > 0) {
    fileCount = `${selected.length} selected`;
  }
  return (
    <Box height="100%" display="flex" flexDirection="column">
      <Box display="flex" justifyContent="space-between" alignItems="center" py={1} px={1.5}>
        <Typography variant="body3" fontSize={14}>
          {fileCount}
        </Typography>
        <Box>{actions}</Box>
      </Box>
      {view === "list" ? (
        <ListView {...commonProps} columnDefs={listViewColumnDefs} pagination={listViewPagination} setSort={setSort} />
      ) : (
        <GalleryView {...commonProps} />
      )}
      <FileViewerModal files={files} getRows={getRows} total={total} initialSlide={initialSlide} onClose={handleZoomClose} open={isZoomOpen} />
    </Box>
  );
};

export default FilesViewer;
